import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState, useEffect } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';
import { scrollToTop } from '../../utils/scrollUtils';

export default function WholeLifeInsurance() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);
  
  // Ensure scroll position is reset to top when component mounts
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="whole-life-insurance" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Whole Life Insurance</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Guaranteed Lifelong Protection with Level Premiums and Cash Value Growth
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is Whole Life Insurance? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Whole Life Insurance?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Whole Life insurance is a type of permanent life insurance that provides coverage for your entire lifetime, as long as premiums are paid. It is characterized by guarantees: the death benefit is guaranteed, the premiums are guaranteed to remain level for life, and the policy builds a guaranteed cash value that grows tax-deferred over time.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                It's often chosen by individuals seeking long-term security, predictable costs, and a way to build tax-advantaged savings alongside lifelong insurance protection. It's a cornerstone product for estate planning and leaving a legacy.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Policy Purchase:</strong> You apply for a policy with a specific death benefit amount.
                </li>
                <li>
                  <strong>Level Premium Payments:</strong> You pay a fixed premium regularly (monthly or annually). This premium amount is guaranteed never to increase for the life of the policy.
                </li>
                <li>
                  <strong>Coverage for Life:</strong> The policy remains in force for your entire lifetime, provided premiums are paid.
                </li>
                <li>
                  <strong>Cash Value Accumulation:</strong> A portion of each premium payment contributes to the policy's cash value, which grows at a guaranteed rate on a tax-deferred basis.
                </li>
                <li>
                  <strong>Accessing Cash Value (Optional):</strong> You can typically borrow against the cash value through policy loans or, in some cases, make withdrawals (though this can reduce the death benefit and may have tax implications).
                </li>
                 <li>
                  <strong>Death Benefit Payout:</strong> Upon the insured's death, the guaranteed death benefit is paid out tax-free to the named beneficiaries. Outstanding policy loans will reduce the final payout.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Lifelong Guaranteed Coverage:</strong> Provides permanent protection that won't expire.</li>
                <li><strong>Guaranteed Level Premiums:</strong> Premiums are fixed and predictable for the life of the policy.</li>
                <li><strong>Guaranteed Cash Value Growth:</strong> Builds a secure, tax-deferred asset over time.</li>
                <li><strong>Potential Access to Funds:</strong> Cash value can be accessed via loans for opportunities or emergencies.</li>
                <li><strong>Estate Planning Tool:</strong> Ideal for covering final expenses, estate taxes, or leaving a guaranteed legacy.</li>
                <li><strong>Simplicity and Certainty:</strong> Offers straightforward guarantees without market volatility affecting the core benefits.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to evaluate:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Higher Initial Premiums:</strong> Premiums are significantly higher than term insurance for the same initial death benefit because they are designed to cover a lifetime and build cash value.</li>
                <li><strong>Lower Initial Coverage per Dollar:</strong> Compared to term, you get less initial death benefit for the same premium amount.</li>
                <li><strong>Cash Value Growth Rate:</strong> Guaranteed growth rates are typically conservative.</li>
                <li><strong>Less Flexibility:</strong> Compared to Universal Life, standard Whole Life offers less flexibility in premium payments or death benefit adjustments.</li>
                <li><strong>Policy Loans Have Interest:</strong> Loans against cash value accrue interest, and unpaid loans reduce the death benefit.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Whole Life Insurance Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Whole Life insurance is often suitable for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Individuals seeking guaranteed, lifelong insurance protection with predictable costs.</li>
                <li>Those prioritizing certainty and safety in their long-term financial planning.</li>
                <li>People looking for a disciplined way to save and build tax-deferred cash value.</li>
                <li>Individuals planning for final expenses, estate settlement costs, or leaving a financial legacy.</li>
                <li>Business owners needing permanent funding solutions for buy-sell agreements or key person protection.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Secure Lifelong Protection with Guarantees</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Explore the certainty and long-term value of Whole Life insurance. Let's discuss how it can fit into your financial plan.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Whole Life Options
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}
